import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import type { BillingEvent, BudgetType, OptimizationGoal } from '../../client'
import {
  DEFAULT_ADSET_NAME,
  GOAL_BILLING,
  OBJECTIVE_GOALS,
  OPTIMIZATION_GOALS,
  currencySymbol,
  enumLabel,
  toDatetimeLocal,
  type PromotionForm,
} from '../../lib/adPromotion'
import LocationPicker from './LocationPicker'

const INPUT =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none'
const TRIGGER =
  'group flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
const PANEL =
  'z-[60] w-(--button-width) divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:4px] [--anchor-max-height:10rem]'
const OPTION =
  'cursor-pointer px-3 py-1.5 text-sm text-gray-800 transition hover:bg-gray-50 data-selected:bg-indigo-50 data-selected:text-indigo-700'

function Label({ children, hint }: { children: string; hint?: React.ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium text-gray-700">
      {children} <span className="text-gray-400">[Ad Set level]</span>
      {hint}
    </label>
  )
}

function Select<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: T[]
  onChange: (value: T) => void
}) {
  return (
    <Listbox value={value} onChange={onChange}>
      <ListboxButton className={TRIGGER}>
        <span className="text-gray-800">{enumLabel(value)}</span>
        <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-400 transition-transform group-data-open:rotate-180" />
      </ListboxButton>
      <ListboxOptions anchor="bottom start" className={PANEL}>
        {options.map((option) => (
          <ListboxOption key={option} value={option} className={OPTION}>
            {enumLabel(option)}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  )
}

type Props = {
  brandId: number
  form: PromotionForm
  newCampaign: boolean
  currency: string
  onChange: (patch: Partial<PromotionForm>) => void
}

export default function AdsetConfig({ brandId, form, newCampaign, currency, onChange }: Props) {
  const lifetime = form.budgetType === 'LIFETIME'
  const goals = newCampaign ? OBJECTIVE_GOALS[form.objective] : OPTIMIZATION_GOALS

  function changeBudgetType(budgetType: BudgetType) {
    if (budgetType !== 'LIFETIME') {
      onChange({ budgetType })
      return
    }

    const end = new Date()
    end.setDate(end.getDate() + 1)
    onChange({
      budgetType,
      startTime: form.startTime || toDatetimeLocal(new Date()),
      endTime: form.endTime || toDatetimeLocal(end),
    })
  }

  function changeGoal(optimizationGoal: OptimizationGoal) {
    const allowed = GOAL_BILLING[optimizationGoal]
    const billingEvent = allowed.includes(form.billingEvent) ? form.billingEvent : allowed[0]
    onChange({ optimizationGoal, billingEvent })
  }

  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold text-gray-900">Ad Set Configuration</h3>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label>Ad Set name</Label>
          <input
            type="text"
            value={form.adsetName}
            placeholder={DEFAULT_ADSET_NAME}
            onChange={(event) => onChange({ adsetName: event.target.value })}
            className={INPUT}
          />
        </div>

        <div>
          <Label>Budget</Label>
          <div className="flex items-center overflow-hidden rounded-lg border border-gray-300 text-sm">
            <span className="pl-3 text-gray-400 select-none">{currencySymbol(currency)}</span>
            <input
              type="number"
              min="1"
              value={form.budget}
              placeholder="100"
              onChange={(event) => onChange({ budget: Number(event.target.value) })}
              className="min-w-0 flex-1 px-2 py-2 text-sm focus:outline-none"
            />
            <div className="shrink-0 border-l border-gray-300">
              <Listbox value={form.budgetType} onChange={changeBudgetType}>
                <ListboxButton className="group flex cursor-pointer items-center gap-1.5 bg-gray-50 py-2 pr-2 pl-3 text-sm text-gray-600 focus:outline-none">
                  {lifetime ? 'Lifetime' : 'Daily'}
                  <ChevronDownIcon className="size-3.5 text-gray-400 transition-transform group-data-open:rotate-180" />
                </ListboxButton>
                <ListboxOptions
                  anchor="bottom end"
                  className="z-[60] divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:4px] [--anchor-max-height:10rem]"
                >
                  <ListboxOption value="DAILY" className={OPTION}>
                    Daily
                  </ListboxOption>
                  <ListboxOption value="LIFETIME" className={OPTION}>
                    Lifetime
                  </ListboxOption>
                </ListboxOptions>
              </Listbox>
            </div>
          </div>
        </div>

        <div>
          <Label>Targeting — Locations</Label>
          <LocationPicker
            brandId={brandId}
            locations={form.targetingLocations}
            onChange={(targetingLocations) => onChange({ targetingLocations })}
          />
        </div>

        <div>
          <Label>Billing event</Label>
          <Select<BillingEvent>
            value={form.billingEvent}
            options={GOAL_BILLING[form.optimizationGoal]}
            onChange={(billingEvent) => onChange({ billingEvent })}
          />
        </div>

        <div>
          <Label>Bid strategy</Label>
          <Select value="LOWEST_COST_WITHOUT_CAP" options={['LOWEST_COST_WITHOUT_CAP']} onChange={() => undefined} />
        </div>

        <div>
          <Label>Optimization goal</Label>
          <Select<OptimizationGoal> value={form.optimizationGoal} options={goals} onChange={changeGoal} />
        </div>

        <div>
          <Label hint={<span className="text-gray-400"> (optional)</span>}>Start time</Label>
          <input
            type="datetime-local"
            value={form.startTime}
            onChange={(event) => onChange({ startTime: event.target.value })}
            className={`${INPUT} ${form.startTime ? 'text-gray-900' : 'text-gray-400'}`}
          />
        </div>

        <div>
          <Label
            hint={lifetime ? <span className="ml-0.5 text-red-500">*</span> : <span className="text-gray-400"> (optional)</span>}
          >
            End time
          </Label>
          <input
            type="datetime-local"
            value={form.endTime}
            onChange={(event) => onChange({ endTime: event.target.value })}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none ${
              lifetime && !form.endTime ? 'border-red-300' : 'border-gray-300'
            } ${form.endTime || lifetime ? 'text-gray-900' : 'text-gray-400'}`}
          />
        </div>
      </div>

      <p className="mt-4 text-xs text-gray-400">Your ad set will be created and activated.</p>
    </div>
  )
}
